import { useEffect, useState, useCallback, useRef } from 'react';
import referralService from '@/services/referral/referralService';

// permissions
import { useAbility } from '@/providers/';

import { toast } from 'sonner';
import { KeenIcon, DefaultTooltip } from '@/components';
import { Can } from '@casl/react';

import { Container } from '@/components/container';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";


import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';

// data tables import
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';


import { SelectInput, REFRAL_REQUEST_STATUS_OPTIONS, FilterContainer, ColumnToggleDropdown } from '@/components/TableFilter'
import { cn } from '@/lib/utils';
import { FiltersToFormData, getAccessToken } from '@/utils';
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { goToLogin, goTo403 } from "@/utils/Navigation";

import { createRoot } from 'react-dom/client';
import { UserRoundCheck, UserRoundX } from 'lucide-react';
import { useAuthContext } from '@/auth';

// init
DataTable.use(DT);

const ReferralRequest = () => {
    const { logout } = useAuthContext();
    const [filterData, setFilterData] = useState({
        status: null, //'pending'
    });
    const tableRef = useRef(null);
    const API_URL = import.meta.env.VITE_APP_BASE_URL;
    const token = getAccessToken();
    const filterRef = useRef(filterData);
    const [showFilter, setShowFilter] = useState(false)
    const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
    const { ability } = useAbility();

    useEffect(() => {
        filterRef.current = filterData;
    }, [filterData]);


    const badgeClasses = {
        pending: 'badge-warning',
        approved: 'badge-success',
        rejected: 'badge-danger',
    };

    // set columns for data table
    const columns = [
        // {
        //     title: 'S#',
        //     name: 'serial',
        //     data: null,
        //  orderable: true,se,
        //     render: (data, type, row, meta) => meta.row + 1,
        // },
        {
            title: 'Name',
            data: null,
            name: 'first_name',
            render: function (data, type, row) {
                return `${row.first_name} ${row.last_name}`;
            }
        },
        { title: 'Email', name: 'email', data: 'email', defaultContent: '-' },
        { title: 'Phone', name: 'number', data: 'number', defaultContent: '-' },
        { title: 'State', name: 'state', data: 'state', defaultContent: '-' },
        { title: 'Zip Code', name: 'zip', data: 'zip', defaultContent: '-' },
        {
            title: 'Comments',
            name: 'Comments',
            data: 'Comments',
            render: function (data, type, row) {
                return `<div id="comments-cell-${row.id}"></div>`;
            }
        },
        {
            title: 'Status',
            name: 'status',
            data: 'status',
            orderable: true,
            render: function (value) {
                return `<span class="badge badge-pill badge-outline capitalize ${badgeClasses[value]} gap-1 items-center"><span class="badge badge-dot size-1.5 ${badgeClasses[value]}"></span>${value}</span>`;
            }
        },
        { title: 'Created At', name: 'created_at', data: 'created_at', defaultContent: '-' },
        {
            title: 'Actions',
            name: 'actions',
            data: null,
            orderable: false,
            searchable: false,
            render: function (data, type, row) {
                return `<div id="action-cell-${row.id}"> </div>`;
            }
        },

        // --- EXTENDED COLUMNS ---
        // { data: 'id', title: 'ID', name: 'id' },
        // { data: 'uuid', title: 'UUID', name: 'uuid' },
        // { data: 'deleted_at', title: 'Deleted At', name: 'deleted_at' },
        // { data: 'updated_at', title: 'Updated At', name: 'updated_at' },
        { data: 'address', title: 'Address', name: 'address', render: (value) => value || '-' },
        { data: 'city', title: 'City', name: 'city', render: (value) => value || '-' },
        { data: 'name', title: 'Name Key', name: 'name', render: (value) => value || '-' },
        { data: 'DT_RowIndex', title: 'Row Index', name: 'DT_RowIndex', render: (value) => value || '-' },
    ];

    const defaultVisibleColumns = [
        // 'serial',
        'first_name',
        'email',
        'number',
        'state',
        'zip',
        'Comments',
        'status',
        'created_at',
        'actions'
    ];

    const ajaxConfig = createDataTableConfig('/referral-request-list', filterRef, token, logout);

    const options = createDataTableOptions({
        order: [[7, 'desc']],

        initComplete: function () {
            const api = this.api();
            api.columns().every(function () {
                const col = this;
                const name = col.settings()[0].aoColumns[col.index()].name;
                if (name && !defaultVisibleColumns.includes(name)) {
                    col.visible(false);
                }
            });
        },

        drawCallback: function () {
            const api = this.api();
            const rows = api.rows({ page: 'current' }).data();
            rows.each((row) => {

                const actionContainer = document.getElementById(`action-cell-${row.id}`);
                if (actionContainer) {
                    if (!actionContainer._reactRoot) {
                        actionContainer._reactRoot = createRoot(actionContainer);
                    }
                    actionContainer._reactRoot.render(renderCustomActions(row));
                }
                const descriptionContainer = document.getElementById(`comments-cell-${row.id}`);
                if (descriptionContainer) {
                    if (!descriptionContainer._reactRoot) {
                        descriptionContainer._reactRoot = createRoot(descriptionContainer);
                    }
                    descriptionContainer._reactRoot.render(renderDescription(row));
                }
            });
        },
    });

    const renderDescription = (row) => {
        const { Comments } = row;
        if (Comments === null) {
            return <span className="text-xs">-</span>;
        }
        return (
            <DefaultTooltip title={Comments} placement="top">
                <span className='line-clamp-2'>{Comments}</span>
            </DefaultTooltip>
        );
    };

    const resetFilters = () => {
        const cleared = {
            status: 'pending',
        };

        setFilterData(cleared);
        filterRef.current = cleared;
        refresh()
    };

    const handleFilterChange = (key, val) => {
        setFilterData((prev) => {
            const updated = { ...prev, [key]: val };
            filterRef.current = updated;
            //console.log('updated',updated)
            return updated;
        });
    };

    const handleToggleFilter = () => {
        setShowFilter(prev => !prev)
    }

    const refresh = () => {
        const table = tableRef.current.dt();
        table.ajax.reload();
    };

    const referralReq = async (actionId, payload) => {
        const updatedPayload = {
            status: payload
        }
        closeModal();
        try {
            const { status, data } = await referralService.updateReferral(updatedPayload, actionId);
            if (status) {
                refresh()
                var msg = '';
                msg = (payload == 'approved') ? 'Request Approved Successfully' : 'Request Rejected Successfully';

                toast.success(`${msg}`);
            }
        } catch (error) {
            console.error('Error', error)

            toast.error('Oops! Something went wrong.', {
                description: 'An error occurred while updating. Please try again in a moment.',
                action: {
                    label: 'Ok',
                },
            });
        }
    };


    // Custom Render Func For Jquery Data tbale
    const renderCustomActions = (row) => {
        const { id, status } = row;
        const isEditable = status === 'approved' || status === 'pending';

        return (
            <>
                <DropdownMenu>
                    <Can I="handle" a="referral-request" ability={ability}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-gray-600">
                                <KeenIcon icon="dots-vertical" className="text-[18px]" />
                            </Button>
                        </DropdownMenuTrigger>
                    </Can>
                    <DropdownMenuContent align="end" className="w-40">
                        <Can I="handle" a="referral-request" ability={ability}>
                            {(status !== 'approved') && (
                                <DropdownMenuItem onClick={() => referralReq(id, 'approved')}>
                                    <UserRoundCheck className="mr-0.5 h-4 w-4 cursor-pointer" /> Approve
                                </DropdownMenuItem>
                            )}
                            {status !== 'rejected' && (
                                <DropdownMenuItem onClick={() => referralReq(id, 'rejected')}>
                                    <UserRoundX className="mr-0.5 h-4 w-4 cursor-pointer" /> Reject
                                </DropdownMenuItem>
                            )}
                        </Can>
                    </DropdownMenuContent>
                </DropdownMenu>
            </>
        );
    };
    return (
        <>
            <Container>
                <FilterContainer
                    show={showFilter}
                    onApply={() => refresh()}
                    onReset={resetFilters}
                >
                    <SelectInput
                        label="Status"
                        value={filterData.status}
                        onChange={(val) => handleFilterChange('status', val)}
                        staticOptions={REFRAL_REQUEST_STATUS_OPTIONS}
                        customClass="sm:w-[32%] w-full"
                    />

                    {/* <TextInput
                        label="Zip Code"
                        value={filterData.zip}
                        onChange={(val) => handleFilterChange('zip', val)}
                        placeholder="Enter zip"
                        customClass="sm:w-[32%] w-full"
                        type="number"
                    /> */}
                </FilterContainer>

                {/* body */}
                <div className="card min-w-full mb-4">
                    <div className="card-header !block">
                        <div className='flex items-center justify-between flex-wrap'>
                            <div className='flex items-center gap-1'>
                                <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
                                {/* <h3 className="card-title">Attorney Registration Requests</h3> */}
                            </div>

                            <div className='flex items-center flex-wrap gap-2'>
                                {/* <Can I="create" a="users" ability={ability}> */}

                                {/* </Can> */}
                                <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
                                    <KeenIcon icon="filter" className="w-fit btn-icon" />
                                    {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <DataTable
                        id="referral-request-table"
                        ajax={ajaxConfig}
                        columns={columns}
                        options={options}
                        ref={tableRef}
                    />

                    {/* <ConfirmationModal
                        show={isOpen}
                        onClose={closeModal}
                        onConfirm={referralReq}
                        message="Are you sure you want to update"
                        title="Are You Sure?"
                    /> */}
                </div>
            </Container>
        </>
    )
}

export { ReferralRequest }
